import { ErrorMessage } from '@hookform/error-message';
import { QuestionAnswer } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  TextField,
} from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '~/components/GenericForm/FormError';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';

import { useSendMessageToHRDialogContent } from './useSendMessageToHRDialogContent';

export interface Props {
  onClose: () => void;
  companyHrContacts?: string[];
}

export const SendMessageToHRDialogContent: React.FC<Props> = ({
  onClose,
  companyHrContacts,
}) => {
  const { t } = useTranslation();

  const {
    isSubmitting,
    register,
    contacts,
    handleContactsChange,
    handleFormSubmit,
    errors,
  } = useSendMessageToHRDialogContent(onClose, companyHrContacts);

  return (
    <form onSubmit={handleFormSubmit} data-cy="message-to-hr-dialog">
      <SolaForceDialogTitle onClose={onClose} icon={<QuestionAnswer />}>
        {t('messageToHR')}
      </SolaForceDialogTitle>
      <Divider />
      <DialogContent>
        {companyHrContacts && companyHrContacts.length > 1 && (
          <Autocomplete
            multiple
            options={companyHrContacts}
            getOptionLabel={(option) => option}
            value={contacts || []}
            onChange={handleContactsChange}
            renderInput={(params) => (
              <TextField
                {...params}
                name="contacts"
                variant="standard"
                label={t('contacts')}
                fullWidth={true}
                margin="normal"
                error={!!errors.contacts}
                helperText={
                  errors.contacts && (
                    <ErrorMessage errors={errors} name="contacts" />
                  )
                }
              />
            )}
            data-cy="contacts"
          />
        )}

        <TextField
          {...register('message')}
          type="text"
          label={t('message')}
          multiline={true}
          fullWidth={true}
          autoFocus={true}
          margin="normal"
          error={!!errors.message}
          helperText={
            errors.message && <ErrorMessage errors={errors} name="message" />
          }
          data-cy="message"
        />

        <FormError errors={errors} dataCy="form-errors" />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('cancel')}
        </Button>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          data-cy="send"
        >
          {t('send')}
        </Button>
      </DialogActions>
    </form>
  );
};
